import useGlobalState from '@hooks/useGlobalState';
import useMolocoLogger from '@hooks/useMolocoLogger';
import { sendMolocoImpTracker } from '@tags/moloco';
import { MolocoAdProducts } from '@wconcept/wconcept-common/models/display/search';
import Box from '@wconcept/wconcept-ui-renewal/src/components/atoms/Box';
import PrdcUnitItem from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/PrdcUnitItem';
import RecProductList from '@wconcept/wconcept-ui-renewal/src/components/organisms/_list/RecProductList';
import { getExclusiveEmblemProp } from '@utils/productExclusiveUtil';
import { useCallback, useEffect, useRef } from 'react';

interface MolocoRecProductListProps {
  type?: string;
  className?: string;
  bgColor?: boolean;
  list?: MolocoAdProducts[];
  isNativeAppBoth: boolean;
  title?: string;
  subTitle?: string;
  onClickItem?: (value: MolocoAdProducts, no: number) => void;
  onClickHeart?: (
    product: MolocoAdProducts,
    active: boolean,
    index: number
  ) => void;
  style?: React.CSSProperties;
  priority?: boolean;
  showExclusiveEmblem?: boolean;
  productType?: 'simple' | 'simple-small';
}

function MolocoRecProductListV2({
  type,
  className,
  bgColor,
  list,
  isNativeAppBoth,
  title,
  subTitle,
  onClickItem,
  onClickHeart,
  style,
  priority,
  showExclusiveEmblem,
  productType = 'simple',
}: MolocoRecProductListProps) {
  const itemsRef = useRef<HTMLDivElement[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isTouching = useRef(false);
  const { myHeartList, isMobile, assistData } = useGlobalState();
  const { sendMolocoDecisionClickTracker } = useMolocoLogger();
  // 스크롤 발생 여부를 추적하는 ref 추가
  const hasScrolled = useRef(false);

  /** 가로 스크롤 컨테이너 안에서 1픽셀이라도 보이는 상품들의 imp 트래커 */
  const getVisibleImpUrls = useCallback(() => {
    const container = scrollContainerRef.current;
    const visibleLeft = container?.scrollLeft || 0;
    const visibleRight = visibleLeft + (container?.offsetWidth || 0);
    const urls: string[] = [];
    for (let i = 0; i < itemsRef.current.length; i++) {
      const item = itemsRef.current[i];
      const itemLeft = item.offsetLeft;
      const itemRight = itemLeft + item.offsetWidth;
      const impUrl = list?.[i]?.impTrackers?.[0];
      if (itemRight > visibleLeft && itemLeft < visibleRight && impUrl) {
        urls.push(impUrl);
      }
    }
    return urls;
  }, [list]);

  const sendVisibleImp = useCallback(() => {
    const urls = getVisibleImpUrls();
    if (urls.length) {
      sendMolocoImpTracker(isNativeAppBoth, urls);
    }
  }, [getVisibleImpUrls, isNativeAppBoth]);

  const onIntersection = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting) {
        sendVisibleImp();
      }
    },
    [sendVisibleImp]
  );

  useEffect(() => {
    const callbackFunction = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      onIntersection(entry);
    };

    let observerRefValue: Element | null = null;

    const observer = new IntersectionObserver(callbackFunction, {
      threshold: 0,
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
      observerRefValue = containerRef.current;
    }

    return () => {
      if (observerRefValue) observer.unobserve(observerRefValue);
    };
  }, [onIntersection]);

  const handleTracker = useCallback(() => {
    if (isTouching.current || !hasScrolled.current) {
      return;
    }
    timeoutRef.current = setTimeout(() => {
      sendVisibleImp();
    }, 200); // 200ms delay to consider scroll as stopped
  }, [sendVisibleImp]);

  const onTouchEnd = useCallback(() => {
    if (timeoutRef.current) {
      isTouching.current = false;
      clearTimeout(timeoutRef.current);
      handleTracker();
    }
    hasScrolled.current = false;
  }, [handleTracker]);

  const onScroll = useCallback(() => {
    hasScrolled.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      handleTracker();
    }, 200); // 200ms delay to consider scroll as stopped
  }, [handleTracker]);

  const onTouchStart = useCallback(() => {
    isTouching.current = true;
    hasScrolled.current = false; // 터치 시작시 스크롤 상태 초기화
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  if (!list) {
    return null;
  }
  return (
    <Box
      ref={containerRef}
      as={type === 'myheart' ? undefined : 'section'}
      className={
        type === 'myheart'
          ? className
          : ['corner-recommend', className].filter(Boolean).join(' ')
      }
      style={style}
    >
      <RecProductList
        bgColor={bgColor}
        isAd
        title={title}
        subTitle={subTitle}
        onScroll={onScroll}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        scrollContainerRef={scrollContainerRef}
      >
        {list?.map((v, i) => {
          if (i > 20) return null;
          return (
            <PrdcUnitItem
              isDesktop={!isMobile}
              type={productType}
              ref={el => {
                if (el) {
                  itemsRef.current[i] = el;
                }
              }}
              isLogin={assistData.isLogin}
              priority={priority ? i < 4 : false}
              // eslint-disable-next-line react/no-array-index-key
              key={`moloco_ad_prodcut${i}`}
              itemCd={v.itemCd}
              finalPrice={v.finalPrice}
              finalDiscountRate={v.finalDiscountRate}
              imageUrl={v.imageUrlMobile}
              infoTags={v.infoTags}
              exclusive={getExclusiveEmblemProp(v, showExclusiveEmblem)}
              brandNameKr={v.brandNameKr}
              myHeartItem={
                v?.itemCd ? !!myHeartList?.product?.has(v?.itemCd) : false
              }
              itemName={v.itemName}
              onClick={() => {
                sendMolocoDecisionClickTracker(v.clickTrackers[0]);
                onClickItem?.(v, i);
              }}
              onClickHeart={async (itemCd, active) =>
                onClickHeart?.(v, active, i)
              }
            />
          );
        })}
      </RecProductList>
    </Box>
  );
}

export default MolocoRecProductListV2;
